import type { BacktestPoint } from '@et/shared';
import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { dateTime, money } from '../lib/format';
import { useT } from '../lib/i18n';

/**
 * Strategy equity vs. the same capital held in BTC — one axis (both in USDT from the same
 * start), strategy in the series colour, the benchmark as a recessive dashed line, a legend,
 * a crosshair tooltip and a table view. Numbers only place pixels; labels read the strings.
 */

const W = 720;
const H = 260;
const PAD = { top: 12, right: 12, bottom: 24, left: 64 };
const num = (v: string): number => Number.parseFloat(v);

export function BacktestChart({ points }: { points: BacktestPoint[] }): JSX.Element {
  const [hover, setHover] = useState<number | null>(null);
  const [asTable, setAsTable] = useState(false);
  const box = useRef<SVGSVGElement>(null);
  const t = useT();

  const geo = useMemo(() => {
    if (points.length < 2) return null;
    const xs = points.map((p) => p.t);
    const all = points.flatMap((p) => [num(p.equity), ...(p.btcHold ? [num(p.btcHold)] : [])]);
    let y0 = Math.min(...all);
    let y1 = Math.max(...all);
    const pad = (y1 - y0) * 0.08 || 1;
    y0 -= pad;
    y1 += pad;
    const x0 = xs[0] ?? 0;
    const x1 = xs[xs.length - 1] ?? 1;
    const sx = (x: number): number => PAD.left + ((x - x0) / (x1 - x0 || 1)) * (W - PAD.left - PAD.right);
    const sy = (y: number): number => PAD.top + (1 - (y - y0) / (y1 - y0)) * (H - PAD.top - PAD.bottom);
    const line = (pick: (p: BacktestPoint) => string | null): string => {
      let d = '';
      points.forEach((p, i) => {
        const v = pick(p);
        if (v === null) return;
        d += `${d === '' ? 'M' : 'L'}${sx(xs[i] ?? 0).toFixed(1)},${sy(num(v)).toFixed(1)} `;
      });
      return d;
    };
    const ticks = Array.from({ length: 5 }, (_, i) => y0 + ((y1 - y0) * i) / 4);
    return { xs, sx, sy, strategy: line((p) => p.equity), btc: line((p) => p.btcHold), ticks, x0, x1 };
  }, [points]);

  if (!geo) return <div className="empty">{t('Not enough data for a chart.')}</div>;

  const onMove = (e: PointerEvent<SVGSVGElement>): void => {
    const rect = box.current?.getBoundingClientRect();
    if (!rect) return;
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let best = 0;
    for (let i = 1; i < geo.xs.length; i++) if (Math.abs(geo.sx(geo.xs[i] ?? 0) - x) < Math.abs(geo.sx(geo.xs[best] ?? 0) - x)) best = i;
    setHover(best);
  };
  const hp = hover === null ? null : points[hover];
  const hx = hover === null ? 0 : geo.sx(geo.xs[hover] ?? 0);

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <div className="row legend">
          <span><i className="swatch strategy" /> {t('Strategy')}</span>
          <span><i className="swatch benchmark" /> {t('Holding BTC instead')}</span>
        </div>
        <button className="small" onClick={() => { setAsTable(!asTable); }}>{asTable ? t('Chart') : t('Table')}</button>
      </div>
      {asTable ? (
        <div className="table-wrap" style={{ maxHeight: 280 }}>
          <table>
            <thead><tr><th>{t('Time')}</th><th className="num">{t('Strategy')}</th><th className="num">{t('Holding BTC instead')}</th></tr></thead>
            <tbody>{points.map((p) => <tr key={p.t}><td>{dateTime(new Date(p.t).toISOString())}</td><td className="num">{money(p.equity)}</td><td className="num">{money(p.btcHold)}</td></tr>)}</tbody>
          </table>
        </div>
      ) : (
        <div className="chart-box">
          <svg ref={box} className="chart" viewBox={`0 0 ${String(W)} ${String(H)}`} role="img" aria-label={t('Strategy equity compared with holding BTC')} onPointerMove={onMove} onPointerLeave={() => { setHover(null); }}>
            {geo.ticks.map((v) => (
              <g key={v}>
                <line className="gridline" x1={PAD.left} x2={W - PAD.right} y1={geo.sy(v)} y2={geo.sy(v)} />
                <text className="axis-label" x={PAD.left - 8} y={geo.sy(v) + 4} textAnchor="end">{money(v.toFixed(0), 0)}</text>
              </g>
            ))}
            <text className="axis-label" x={PAD.left} y={H - 6}>{dateTime(new Date(geo.x0).toISOString())}</text>
            <text className="axis-label" x={W - PAD.right} y={H - 6} textAnchor="end">{dateTime(new Date(geo.x1).toISOString())}</text>
            <path className="line benchmark" d={geo.btc} />
            <path className="line" d={geo.strategy} />
            {hp && <line className="crosshair" x1={hx} x2={hx} y1={PAD.top} y2={H - PAD.bottom} />}
            {hp && <circle className="marker" cx={hx} cy={geo.sy(num(hp.equity))} r={5} />}
            <rect x={PAD.left} y={0} width={W - PAD.left - PAD.right} height={H} fill="transparent" />
          </svg>
          {hp && (
            <div className="chart-tip" style={{ left: `${String((hx / W) * 100)}%`, top: `${String((geo.sy(num(hp.equity)) / H) * 100)}%` }}>
              <div className="muted">{dateTime(new Date(hp.t).toISOString())}</div>
              <div>{t('Strategy')}: <b>{money(hp.equity)}</b></div>
              <div className="muted">{t('Holding BTC instead')}: {money(hp.btcHold)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
